import React, { useState, useEffect } from 'react';
import {
  Settings2, EyeOff, Code2, X, Check, Copy,
  Plane, BookOpen, Mountain, Cpu, ChevronDown,
  RotateCcw, Share2, Monitor, Tablet, Smartphone, Maximize
} from 'lucide-react';
import { AdSlotConfig } from './GalleryAdSlot';

/* eslint-disable @typescript-eslint/no-unused-vars */

export type PublisherTemplate = 'news' | 'lifestyle' | 'sports' | 'technology';
export type DeviceMode = 'desktop' | 'tablet' | 'mobile';

interface GalleryToolbarProps {
  isEditing: boolean;
  onToggleEditing: () => void;
  activeTemplate: PublisherTemplate;
  onTemplateChange: (template: PublisherTemplate) => void;
  adSlots: AdSlotConfig[];
  onUpdateTag: (slotId: string, tag: string) => void;
  onResetAll: () => void;
  externalEditSlotId: string | null;
  onClearExternalEdit: () => void;
  previewMode?: boolean;
  deviceMode?: DeviceMode;
  onDeviceChange?: (mode: DeviceMode) => void;
  onFullScreen?: () => void;
}

const templates: { id: PublisherTemplate; name: string; icon: React.ElementType; description: string }[] = [
  { id: 'news', name: 'Travel & Culture', icon: Plane, description: 'Meridian-style editorial' },
  { id: 'lifestyle', name: 'Global Affairs', icon: BookOpen, description: 'Atlas-style briefing' },
  { id: 'sports', name: 'Adventure & Sport', icon: Mountain, description: 'Outdoor editorial' },
  { id: 'technology', name: 'Tech & Science', icon: Cpu, description: 'Signal-style publication' },
];

const deviceOptions: { id: DeviceMode; label: string; icon: React.ElementType }[] = [
  { id: 'desktop', label: 'Desktop', icon: Monitor },
  { id: 'tablet', label: 'Tablet', icon: Tablet },
  { id: 'mobile', label: 'Mobile', icon: Smartphone },
];


const GalleryToolbar: React.FC<GalleryToolbarProps> = ({
  isEditing,
  onToggleEditing,
  activeTemplate,
  onTemplateChange,
  adSlots,
  onUpdateTag,
  onResetAll,
  externalEditSlotId,
  onClearExternalEdit,
  previewMode = false,
  deviceMode = 'desktop',
  onDeviceChange,
  onFullScreen,
}) => {
  const [showTagModal, setShowTagModal] = useState(false);
  const [editingSlot, setEditingSlot] = useState<string | null>(null);
  const [tagInput, setTagInput] = useState('');
  const [showTemplateDropdown, setShowTemplateDropdown] = useState(false);
  const [copiedSlot, setCopiedSlot] = useState<string | null>(null);
  const [shareConfirm, setShareConfirm] = useState(false);

  const activeTemplateData = templates.find(t => t.id === activeTemplate) || templates[0];
  const configuredSlots = adSlots.filter(s => s.tag && s.tag.trim().length > 0).length;

  useEffect(() => {
    if (externalEditSlotId) {
      const slot = adSlots.find(s => s.id === externalEditSlotId);
      if (slot) {
        setEditingSlot(externalEditSlotId);
        setTagInput(slot.tag || '');
        setShowTagModal(true);
      }
      onClearExternalEdit();
    }
  }, [externalEditSlotId, adSlots, onClearExternalEdit]);

  const handleOpenTagEditor = (slotId: string) => {
    const slot = adSlots.find(s => s.id === slotId);
    setEditingSlot(slotId);
    setTagInput(slot?.tag || '');
    setShowTagModal(true);
  };

  const handleSaveTag = () => {
    if (editingSlot) {
      onUpdateTag(editingSlot, tagInput);
      setShowTagModal(false);
      setEditingSlot(null);
      setTagInput('');
    }
  };

  const handleCopySlotId = (slotId: string) => {
    navigator.clipboard.writeText(slotId);
    setCopiedSlot(slotId);
    setTimeout(() => setCopiedSlot(null), 2000);
  };

  const handleSharePreview = async () => {
    const shareData = {
      title: 'Ad Preview — Adventive',
      url: window.location.href,
    };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch {
        // User cancelled or share failed — ignore
      }
    } else {
      await navigator.clipboard.writeText(window.location.href);
      setShareConfirm(true);
      setTimeout(() => setShareConfirm(false), 2000);
    }
  };

  const editingSlotData = adSlots.find(s => s.id === editingSlot);

  return (
    <>
      <div className="bg-white/80 dark:bg-slate-800/80 backdrop-blur-xl rounded-2xl border border-slate-200/80 dark:border-slate-700/50 shadow-lg shadow-slate-900/5 dark:shadow-black/20 overflow-hidden">
        {/* Adventive branding bar — single row with device pills, share, fullscreen */}
        <div className="bg-gradient-to-r from-[#635bff] to-[#7c3aed] px-5 py-2.5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 bg-white/20 rounded-md flex items-center justify-center">
                  <Code2 className="w-3.5 h-3.5 text-white" />
                </div>
                <span className="text-sm font-semibold text-white">Ad Preview Controls</span>
              </div>
              {!previewMode && (
                <>
                  <span className="text-xs text-white/60 hidden sm:inline">|</span>
                  <span className="text-xs text-white/70 hidden sm:inline">Configure ad tags and preview templates</span>
                </>
              )}

              {/* Device viewport pills — inline in branding bar */}
              {onDeviceChange && (
                <div className="flex items-center bg-white/10 rounded-lg p-0.5 ml-2">
                  {deviceOptions.map((device) => (
                    <button
                      key={device.id}
                      onClick={() => onDeviceChange(device.id)}
                      className={`flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${
                        deviceMode === device.id
                          ? 'bg-white/20 text-white shadow-sm'
                          : 'text-white/50 hover:text-white/80'
                      }`}
                    >
                      <device.icon className="w-3.5 h-3.5" />
                      <span className="hidden md:inline">{device.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              {!previewMode && (
                <span className="text-xs text-white/70 hidden md:inline">
                  {configuredSlots}/{adSlots.length} slots configured
                </span>
              )}
              <button
                onClick={handleSharePreview}
                className="flex items-center gap-1.5 px-3 py-1 text-xs font-medium text-white/90 hover:text-white bg-white/10 hover:bg-white/20 rounded-md transition-colors"
              >
                {shareConfirm ? <Check className="w-3 h-3" /> : <Share2 className="w-3 h-3" />}
                <span className="hidden sm:inline">{shareConfirm ? 'Copied!' : 'Share'}</span>
              </button>
              {onFullScreen && (
                <button
                  onClick={onFullScreen}
                  className="flex items-center gap-1.5 px-3 py-1 text-xs font-medium text-white/90 hover:text-white bg-white/10 hover:bg-white/20 rounded-md transition-colors"
                >
                  <Maximize className="w-3 h-3" />
                  <span className="hidden sm:inline">Full Screen Preview</span>
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Controls bar — only shown when NOT in preview mode */}
        {!previewMode && (
          <div className="px-5 py-3">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                {/* Template selector */}
                <div className="relative">
                  <button
                    onClick={() => setShowTemplateDropdown(!showTemplateDropdown)}
                    className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 rounded-lg transition-colors"
                  >
                    <activeTemplateData.icon className="w-4 h-4" />
                    <span className="hidden sm:inline">{activeTemplateData.name}</span>
                    <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showTemplateDropdown ? 'rotate-180' : ''}`} />
                  </button>

                  {showTemplateDropdown && (
                    <>
                      <div className="fixed inset-0 z-40" onClick={() => setShowTemplateDropdown(false)} />
                      <div className="absolute top-full left-0 mt-1 w-64 bg-white dark:bg-slate-800 rounded-xl shadow-xl border border-slate-200 dark:border-slate-700 py-1 z-50">
                        <p className="px-3 py-2 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Publisher Template</p>
                        {templates.map((template) => (
                          <button
                            key={template.id}
                            onClick={() => {
                              onTemplateChange(template.id);
                              setShowTemplateDropdown(false);
                            }}
                            className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm transition-colors ${
                              activeTemplate === template.id
                                ? 'bg-[#635bff]/10 text-[#635bff]'
                                : 'text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700/50'
                            }`}
                          >
                            <div className="w-8 h-8 rounded-lg bg-slate-100 dark:bg-slate-700 flex items-center justify-center">
                              <template.icon className="w-4 h-4" />
                            </div>
                            <div className="text-left">
                              <span className="font-medium block">{template.name}</span>
                              <span className="text-[10px] text-slate-400">{template.description}</span>
                            </div>
                            {activeTemplate === template.id && <Check className="w-4 h-4 ml-auto flex-shrink-0" />}
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>

                {/* Edit mode toggle */}
                <button
                  onClick={onToggleEditing}
                  className={`flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                    isEditing
                      ? 'bg-[#635bff] text-white shadow-md shadow-[#635bff]/25'
                      : 'text-slate-600 dark:text-slate-400 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600'
                  }`}
                >
                  {isEditing ? <EyeOff className="w-4 h-4" /> : <Settings2 className="w-4 h-4" />}
                  <span className="hidden sm:inline">{isEditing ? 'Preview Mode' : 'Edit Tags'}</span>
                </button>
              </div>

              {/* Right-side controls */}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => { setEditingSlot(null); setShowTagModal(true); }}
                  className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-slate-600 dark:text-slate-400 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 rounded-lg transition-colors"
                >
                  <Code2 className="w-4 h-4" />
                  <span className="hidden md:inline">Manage Tags</span>
                </button>

                <button
                  onClick={onResetAll}
                  className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-slate-500 dark:text-slate-400 hover:text-red-600 dark:hover:text-red-400 bg-slate-100 dark:bg-slate-700 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                  title="Reset all tags"
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Tag Management Modal */}
      {showTagModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={() => { setShowTagModal(false); setEditingSlot(null); }}>
          <div className="w-full max-w-2xl bg-white dark:bg-slate-900 rounded-2xl shadow-2xl overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-800">
              <div>
                <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
                  {editingSlot ? `Configure: ${editingSlotData?.label}` : 'Ad Tag Manager'}
                </h2>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
                  {editingSlot
                    ? `${editingSlotData?.width} x ${editingSlotData?.height} — Paste your ad tag below`
                    : 'Configure ad tags for each placement slot'}
                </p>
              </div>
              <button
                onClick={() => { setShowTagModal(false); setEditingSlot(null); }}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              >
                <X className="w-5 h-5 text-slate-400" />
              </button>
            </div>

            <div className="p-6 max-h-[60vh] overflow-y-auto">
              {editingSlot ? (
                <div className="space-y-4">
                  <div className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-800 rounded-xl">
                    <div className="w-10 h-10 bg-[#635bff]/10 rounded-lg flex items-center justify-center">
                      <Code2 className="w-5 h-5 text-[#635bff]" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-900 dark:text-white">{editingSlotData?.label}</p>
                      <p className="text-xs text-slate-500 font-mono">{editingSlotData?.width}x{editingSlotData?.height} — ID: {editingSlot}</p>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      Ad Tag (HTML/JavaScript)
                    </label>
                    <textarea
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      placeholder={`<script src="https://ad.adventive.com/tag/${editingSlot}"></script>\n\nor paste any HTML/JavaScript ad tag...`}
                      className="w-full h-48 px-4 py-3 text-sm font-mono bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-[#635bff]/20 focus:border-[#635bff] outline-none resize-none transition-all"
                    />
                  </div>
                  <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-200 dark:border-blue-800">
                    <p className="text-xs text-blue-700 dark:text-blue-400">
                      <strong>Tip:</strong> Paste any standard ad tag — script tags, iframe embeds, or Adventive tags. The tag will render in a sandboxed iframe for security.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  {adSlots.map((slot) => (
                    <div key={slot.id} className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-700/50 transition-colors">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${slot.tag ? 'bg-emerald-100 dark:bg-emerald-900/30' : 'bg-slate-200 dark:bg-slate-700'}`}>
                          <Code2 className={`w-5 h-5 ${slot.tag ? 'text-emerald-600 dark:text-emerald-400' : 'text-slate-400'}`} />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-slate-900 dark:text-white">{slot.label}</p>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className="text-xs text-slate-500 font-mono">{slot.width}x{slot.height}</span>
                            {slot.tag && (
                              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700 dark:text-emerald-400 bg-emerald-100 dark:bg-emerald-900/30 rounded-full">
                                <Check className="w-2.5 h-2.5" /> Active
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleCopySlotId(slot.id)}
                          className="flex items-center gap-1 px-2 py-1.5 text-xs text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg transition-colors"
                        >
                          {copiedSlot === slot.id ? <Check className="w-3 h-3 text-emerald-500" /> : <Copy className="w-3 h-3" />}
                          ID
                        </button>
                        <button
                          onClick={() => handleOpenTagEditor(slot.id)}
                          className="px-3 py-1.5 text-xs font-medium text-[#635bff] bg-[#635bff]/10 hover:bg-[#635bff]/20 rounded-lg transition-colors"
                        >
                          {slot.tag ? 'Edit' : 'Add Tag'}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex items-center justify-between px-6 py-4 border-t border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50">
              {editingSlot ? (
                <>
                  <button
                    onClick={() => { setEditingSlot(null); setTagInput(''); }}
                    className="px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white transition-colors"
                  >
                    Back to All Slots
                  </button>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => { setShowTagModal(false); setEditingSlot(null); }}
                      className="px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSaveTag}
                      className="px-5 py-2 text-sm font-semibold text-white bg-[#635bff] hover:bg-[#5449e6] rounded-lg transition-colors"
                    >
                      Save Tag
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <p className="text-xs text-slate-500">{configuredSlots} of {adSlots.length} slots configured</p>
                  <button
                    onClick={() => setShowTagModal(false)}
                    className="px-5 py-2 text-sm font-semibold text-white bg-[#635bff] hover:bg-[#5449e6] rounded-lg transition-colors"
                  >
                    Done
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default GalleryToolbar;
